import React, { useState, useEffect } from 'react';
import styles from './css/GaugeChart.module.css';
import { LoadingWaves } from '../addons/LoadingWaves';

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: string;
}

interface GaugeData {
    [key: string]: {
      index: number;
      percentage: number;
      color?: string;  // Optional color override
    };
}
  
interface GaugeChartProps {
  data: number | undefined;  // This will be the max value
  needle_value: number;  // This will be the needle value
  second_needle_value?: number;  // Optional second needle
  year: string;
  size?: "small" | "default";
  showAsPercentage?: boolean;
}

const formatNumber = (num: number, showAsPercentage?: boolean): string => {
  if (showAsPercentage) {
    return `${Number.isInteger(num) ? num.toString() : num.toFixed(1)}%`;
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export const GaugeChart: React.FC<GaugeChartProps> = ({ 
  data: max, 
  needle_value: needle, 
  second_needle_value,
  year, 
  size,
  showAsPercentage = false
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [secondAnimatedValue, setSecondAnimatedValue] = useState(0);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [secondTooltipVisible, setSecondTooltipVisible] = useState(false);

  useEffect(() => {
    if (max === undefined) return;
    
    const duration = 1100; // Total animation duration in ms
    const steps = 60; // Number of steps in animation
    const stepDuration = duration / steps;
    
    // Animate first needle
    let current = 0;
    const increment = needle / steps;
    const timer = setInterval(() => {
      current += increment;
      setAnimatedValue(current);
      
      if (current >= needle) {
        setAnimatedValue(needle); // Ensure we end exactly at the target
        clearInterval(timer);
      }
    }, stepDuration);

    // Animate second needle if it exists
    if (second_needle_value !== undefined) {
      let secondCurrent = 0;
      const secondIncrement = second_needle_value / steps;
      const secondTimer = setInterval(() => {
        secondCurrent += secondIncrement;
        setSecondAnimatedValue(secondCurrent);
        
        if (secondCurrent >= second_needle_value) {
          setSecondAnimatedValue(second_needle_value);
          clearInterval(secondTimer);
        }
      }, stepDuration);

      return () => {
        clearInterval(timer);
        clearInterval(secondTimer);
      };
    }
    
    return () => clearInterval(timer);
  }, [max, needle, second_needle_value]);

  if (!max) {
    return (
      <div className={styles.container}>
        <LoadingWaves color="#34D399" />
      </div>
    );
  }

  const center = { x: 110, y: 110 };
  const defaultThreeColors = ['#EC4447', '#FFD324', '#34D399'];
  
  const percentage = max > 0 ? (animatedValue / max) * 100 : 0;
  const needleRotation = -87 + (percentage * 1.8);
  
  const secondPercentage = second_needle_value !== undefined && max > 0 
    ? (secondAnimatedValue / max) * 100 
    : 0;
  const secondNeedleRotation = -87 + (secondPercentage * 1.8);

  const arcs = [
    { color: defaultThreeColors[0], startAngle: -180, endAngle: -120 },
    { color: defaultThreeColors[1], startAngle: -120, endAngle: -60 },
    { color: defaultThreeColors[2], startAngle: -60, endAngle: 0 },
  ];

  // Legend data
  const legendData = [

    { name: '68-100%  חוסן גבוה', color: defaultThreeColors[2] },
    { name: '34-67%  חוסן בינוני', color: defaultThreeColors[1] },
    { name: 'עד 33%  חוסן נמוך', color: defaultThreeColors[0] },

  ];

  const renderLegend = () => {
    return (
      <div className="flex justify-center w-full items-center mb-2">
        <div className="flex flex-row gap-4 items-center justify-center">
          {legendData.map((item, index) => (
            <div key={index} className="flex flex-row items-center gap-1">
              <span 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-[10px] text-[#49454F]">
                {item.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderLegend()}
      <div className={size === "small" ? styles.gaugeWrapperSmall : styles.gaugeWrapper}>
        {/* Gauge SVG */}
        <svg viewBox="0 0 220 120" className={styles.gauge}>
          {arcs.map((arc, index) => (
            <path
              key={index}
              d={`M ${center.x + 90 * Math.cos(arc.startAngle * Math.PI / 180)} ${
                center.y + 90 * Math.sin(arc.startAngle * Math.PI / 180)
              } A 91 90 0 0 1 ${
                center.x + 90 * Math.cos(arc.endAngle * Math.PI / 180)
              } ${center.y + 90 * Math.sin(arc.endAngle * Math.PI / 180)}`}
              stroke={arc.color}
              strokeWidth="40"
              fill="none"
              className={styles.arc}
            />
          ))}

          {/* Second needle (if provided) */}
          {second_needle_value !== undefined && (
            <g
              transform={`rotate(${secondNeedleRotation}, ${center.x}, ${center.y - 5})`}
              className={styles.needle}
              onMouseEnter={() => setSecondTooltipVisible(true)}
              onMouseLeave={() => setSecondTooltipVisible(false)}
            >
              <path
                d={`M${center.x - 4} ${center.y - 95} L${center.x - 4} ${center.y - 5} 
                  C${center.x - 4} ${center.y - 2.2} ${center.x - 2} ${center.y} ${center.x + 1.8} ${center.y} 
                  C${center.x + 4.5} ${center.y} ${center.x + 6.7} ${center.y - 2.2} ${center.x + 6.4} ${center.y - 5} 
                  L${center.x - 4} ${center.y - 95}`}
                fill="#1890FF"

              />
            </g>
          )}

          {/* First needle  */}
          <g
            transform={`rotate(${needleRotation}, ${center.x}, ${center.y - 5})`}
            className={styles.needle}
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
          >
            <path
              d={`M${center.x - 4} ${center.y - 95} L${center.x - 4} ${center.y - 5} 
                C${center.x - 4} ${center.y - 2.2} ${center.x - 2} ${center.y} ${center.x + 1.8} ${center.y} 
                C${center.x + 4.5} ${center.y} ${center.x + 6.7} ${center.y - 2.2} ${center.x + 6.4} ${center.y - 5} 
                L${center.x - 4} ${center.y - 95}`}
              fill="black"
            />
          </g>
        </svg>

        {/* Tooltips */}
        {tooltipVisible && (
          <div className={styles.tooltip}>
            <div>{second_needle_value === undefined ? 'חוסן מצב עתידי:' : 'חוסן מצב קיים:'} {formatNumber(needle, showAsPercentage)}</div>
          </div>
        )}
        {secondTooltipVisible && second_needle_value !== undefined && (
          <div className={styles.tooltip}>
            <div>חוסן מצב עתידי: {formatNumber(second_needle_value, showAsPercentage)}</div>
          </div>
        )}
      </div>

        <div className={styles.minMaxLabels}>
          <span className={styles.maxLabel}>{formatNumber(max, showAsPercentage)}</span>
          <span className={styles.minLabel}>0{showAsPercentage ? '%' : ''}</span>
        </div>
        <div className={styles.yearLabel}>{year}</div>

    </div>
  );
};